/**
 * Toast notification wrapper using react-hot-toast
 * Provides consistent toast styling across the app
 */
import React from 'react';
import toast from 'react-hot-toast';
import CascadeFailedToast from './CascadeFailedToast';
import { colors as theme } from '../theme';

const toastStyle = {
  background: theme.surface1,
  color: theme.text,
  border: `1px solid ${theme.surface3}`,
  borderRadius: '8px',
  padding: '12px 16px',
  fontSize: '14px',
};

/**
 * Show a success toast
 * @param {string} message
 */
export const toastSuccess = (message) => {
  toast.success(message, {
    style: {
      ...toastStyle,
      borderColor: theme.success,
    },
    iconTheme: {
      primary: theme.success,
      secondary: theme.surface1,
    },
  });
};

/**
 * Show an error toast
 * @param {string} message
 */
export const toastError = (message) => {
  toast.error(message, {
    style: {
      ...toastStyle,
      borderColor: theme.danger,
    },
    iconTheme: {
      primary: theme.danger,
      secondary: theme.surface1,
    },
    duration: 5000,
  });
};

/**
 * Show an info toast
 * @param {string} message
 */
export const toastInfo = (message) => {
  toast(message, {
    style: toastStyle,
    icon: 'ℹ️',
  });
};

/**
 * Persistent warning for a forward-week cascade that failed AFTER its primary write had already
 * landed — the coach must not be left believing the change reached the client's current/later
 * weeks. Stays until dismissed or a retry lands.
 * @param {string} message
 * @param {() => Promise<boolean>} retry - re-runs the cascade; resolves true once it landed
 * @returns {string} Toast ID
 */
export const toastCascadeFailed = (message, retry) => {
  return toast.custom(
    (t) => React.createElement(CascadeFailedToast, {
      visible: t.visible,
      message,
      onRetry: async () => {
        const landed = await retry();
        if (landed) {
          toast.dismiss(t.id);
          toastSuccess("Applied to the client's current and later weeks");
        }
        return landed;
      },
      onDismiss: () => toast.dismiss(t.id),
    }),
    { duration: Infinity }
  );
};

/**
 * Show a loading toast (returns toast id for dismissal)
 * @param {string} message
 * @returns {string} Toast ID
 */
export const toastLoading = (message) => {
  return toast.loading(message, {
    style: toastStyle,
  });
};

/**
 * Dismiss a toast by ID
 * @param {string} toastId
 */
export const toastDismiss = (toastId) => {
  toast.dismiss(toastId);
};

export { toast };
